import type { Localized } from '@/i18n/config'

export interface CommonCopy {
    tagline: string
    description: string
    skipToContent: string
    nav: {
        services: string
        servicesIntro: string
        allServices: string
        practiceAreas: string
        featured: string
        industries: string
        aiSolutions: string
        resources: string
        insights: string
        insightsDescription: string
        blog: string
        blogDescription: string
        caseStudies: string
        caseStudiesDescription: string
        about: string
        pricing: string
        contact: string
        clientPortal: string
        clientLogin: string
        bookConsultation: string
        openMenu: string
        closeMenu: string
        language: string
    }
    footer: {
        statement: string
        services: string
        company: string
        resources: string
        portal: string
        markets: string
        marketsList: Array<string>
        office: string
        officeValue: string
        privacy: string
        terms: string
        rights: string
        login: string
        register: string
        portalOverview: string
        brandNote: string
    }
    cta: {
        title: string
        text: string
        primary: string
        secondary: string
    }
    readMore: string
    learnMore: string
    viewAll: string
    backTo: string
    minRead: string
    by: string
    published: string
    breadcrumbHome: string
}

export const common: Localized<CommonCopy> = {
    en: {
        tagline: 'Tax, finance and AI. Connected.',
        description: 'Ura Advisory is an AI-powered tax, finance and business transformation firm in Kosovo, serving local and international businesses with tax, accounting, payroll, CFO and automation services.',
        skipToContent: 'Skip to content',
        nav: {
            services: 'Services',
            servicesIntro: 'Six practice areas, one accountable team and a client portal that keeps every number in view.',
            allServices: 'All services',
            practiceAreas: 'Practice areas',
            featured: 'Featured',
            industries: 'Industries',
            aiSolutions: 'AI Solutions',
            resources: 'Insights',
            insights: 'Insights',
            insightsDescription: 'Outlooks, guides and research on tax and finance.',
            blog: 'Blog',
            blogDescription: 'Practical notes on deadlines, rules and tools.',
            caseStudies: 'Case studies',
            caseStudiesDescription: 'How engagements run from first call to results.',
            about: 'About',
            pricing: 'Pricing',
            contact: 'Contact',
            clientPortal: 'Client Portal',
            clientLogin: 'Client login',
            bookConsultation: 'Book a consultation',
            openMenu: 'Open menu',
            closeMenu: 'Close menu',
            language: 'Language'
        },
        footer: {
            statement: 'Tax, accounting and finance expertise from Kosovo, delivered through intelligent systems to businesses at home and abroad.',
            services: 'Services',
            company: 'Company',
            resources: 'Resources',
            portal: 'Client Portal',
            markets: 'Markets we serve',
            marketsList: ['Kosovo', 'United Kingdom', 'United States', 'Germany', 'Switzerland', 'Austria'],
            office: 'Office',
            officeValue: 'Prishtina, Kosovo',
            privacy: 'Privacy policy',
            terms: 'Terms of service',
            rights: 'All rights reserved.',
            login: 'Log in',
            register: 'Create an account',
            portalOverview: 'Portal overview',
            brandNote: 'Ura means "the bridge" in Albanian.'
        },
        cta: {
            title: 'Start with a 30-minute conversation',
            text: 'Tell us where your finances stand today. We will tell you what we would fix first, what it costs and how quickly we can start.',
            primary: 'Book a consultation',
            secondary: 'See pricing'
        },
        readMore: 'Read more',
        learnMore: 'Learn more',
        viewAll: 'View all',
        backTo: 'Back to',
        minRead: 'min read',
        by: 'By',
        published: 'Published',
        breadcrumbHome: 'Home'
    },
    sq: {
        tagline: 'Tatime, financa dhe AI. Të lidhura.',
        description: 'Ura Advisory është një firmë tatimore, financiare dhe e transformimit të biznesit e fuqizuar nga AI në Kosovë, që u shërben bizneseve vendase dhe ndërkombëtare me tatime, kontabilitet, paga, shërbime CFO dhe automatizim.',
        skipToContent: 'Kalo te përmbajtja',
        nav: {
            services: 'Shërbimet',
            servicesIntro: 'Gjashtë fusha ekspertize, një ekip përgjegjës dhe një portal klienti që i mban të gjitha shifrat para syve.',
            allServices: 'Të gjitha shërbimet',
            practiceAreas: 'Fushat e ekspertizës',
            featured: 'Në fokus',
            industries: 'Industritë',
            aiSolutions: 'Zgjidhje me AI',
            resources: 'Analiza',
            insights: 'Analiza',
            insightsDescription: 'Perspektiva, udhëzues dhe studime për tatimet dhe financat.',
            blog: 'Blog',
            blogDescription: 'Shënime praktike për afatet, rregullat dhe mjetet.',
            caseStudies: 'Raste studimore',
            caseStudiesDescription: 'Si zhvillohet një angazhim, nga biseda e parë te rezultatet.',
            about: 'Rreth nesh',
            pricing: 'Çmimet',
            contact: 'Kontakti',
            clientPortal: 'Portali i klientit',
            clientLogin: 'Hyrja e klientit',
            bookConsultation: 'Rezervo një konsultë',
            openMenu: 'Hap menynë',
            closeMenu: 'Mbyll menynë',
            language: 'Gjuha'
        },
        footer: {
            statement: 'Ekspertizë tatimore, kontabël dhe financiare nga Kosova, e ofruar përmes sistemeve inteligjente për bizneset vendase dhe ato jashtë vendit.',
            services: 'Shërbimet',
            company: 'Kompania',
            resources: 'Burime',
            portal: 'Portali i klientit',
            markets: 'Tregjet ku punojmë',
            marketsList: ['Kosova', 'Mbretëria e Bashkuar', 'Shtetet e Bashkuara', 'Gjermania', 'Zvicra', 'Austria'],
            office: 'Zyra',
            officeValue: 'Prishtinë, Kosovë',
            privacy: 'Politika e privatësisë',
            terms: 'Kushtet e shërbimit',
            rights: 'Të gjitha të drejtat të rezervuara.',
            login: 'Hyr',
            register: 'Krijo llogari',
            portalOverview: 'Njihuni me portalin',
            brandNote: 'Ura, ashtu si në shqip: lidhja mes dy brigjeve.'
        },
        cta: {
            title: 'Filloni me një bisedë 30-minutëshe',
            text: 'Na tregoni ku qëndrojnë financat tuaja sot. Ne ju themi çfarë do të rregullonim së pari, sa kushton dhe sa shpejt mund të fillojmë.',
            primary: 'Rezervo një konsultë',
            secondary: 'Shiko çmimet'
        },
        readMore: 'Lexo më shumë',
        learnMore: 'Mëso më shumë',
        viewAll: 'Shiko të gjitha',
        backTo: 'Kthehu te',
        minRead: 'min lexim',
        by: 'Nga',
        published: 'Publikuar',
        breadcrumbHome: 'Ballina'
    }
}
